import { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Alert, AlertDescription } from '../ui/alert';
import { Key, Copy, Check, AlertTriangle, Loader2, ShieldAlert, User } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../../utils/apiUrl';

const AdminResetPasswordModal = ({ isOpen, onClose, token, targetUser }) => {
  const [forceChangeOnLogin, setForceChangeOnLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [resetResult, setResetResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  const handleReset = async () => {
    setError('');
    setLoading(true);

    try {
      const response = await axios.patch(
        `${API_URL}/auth/admin/reset-password/${targetUser.id}`,
        { force_change_on_login: forceChangeOnLogin },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setResetResult(response.data);
      toast.success('Password reset successfully');
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to reset password';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyPassword = () => {
    if (resetResult?.temporary_password) {
      navigator.clipboard.writeText(resetResult.temporary_password);
      setCopied(true);
      toast.success('Temporary password copied to clipboard');
      setTimeout(() => setCopied(false), 3000);
    }
  };

  const handleClose = () => {
    setResetResult(null);
    setError('');
    setCopied(false);
    setForceChangeOnLogin(true);
    onClose();
  };

  if (!targetUser) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-slate-900 border-white/10 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-amber-400" />
            Reset User Password
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Generate a new temporary password for this user
          </DialogDescription>
        </DialogHeader>

        {!resetResult ? (
          // Step 1: Confirm reset
          <div className="space-y-4 mt-4">
            {error && (
              <Alert className="bg-red-500/10 border-red-500/30">
                <AlertTriangle className="w-4 h-4 text-red-400" />
                <AlertDescription className="text-red-400">{error}</AlertDescription>
              </Alert>
            )}

            {/* Target User Info */}
            <div className="p-4 rounded-lg bg-slate-800/50 border border-white/10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#C5A059]/20 flex items-center justify-center">
                  <User className="w-5 h-5 text-[#C5A059]" />
                </div>
                <div>
                  <p className="text-white font-medium">{targetUser.name}</p>
                  <p className="text-sm text-slate-400">{targetUser.email}</p>
                  <p className="text-xs text-slate-500 capitalize">{targetUser.role}</p>
                </div>
              </div>
            </div>

            {/* Force Change Option */}
            <div className="flex items-center justify-between p-4 rounded-lg bg-slate-800/50 border border-white/10">
              <div>
                <Label className="text-white">Force password change on login</Label>
                <p className="text-xs text-slate-400 mt-1">
                  User will be required to set a new password after logging in
                </p>
              </div>
              <Switch
                checked={forceChangeOnLogin}
                onCheckedChange={setForceChangeOnLogin}
                data-testid="force-change-switch"
              />
            </div>

            <Alert className="bg-amber-500/10 border-amber-500/30">
              <AlertTriangle className="w-4 h-4 text-amber-400" />
              <AlertDescription className="text-amber-300 text-sm">
                This will generate a new temporary password. The user will not be able to login with their old password.
              </AlertDescription>
            </Alert>

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                className="flex-1 border-white/10"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleReset}
                disabled={loading}
                className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="confirm-reset-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Resetting...
                  </>
                ) : (
                  <>
                    <Key className="w-4 h-4 mr-2" />
                    Reset Password
                  </>
                )}
              </Button>
            </div>
          </div>
        ) : (
          // Step 2: Show result
          <div className="space-y-4 mt-4">
            {resetResult.show_password_on_screen && resetResult.temporary_password ? (
              <>
                <Alert className="bg-amber-500/10 border-amber-500/30">
                  <AlertTriangle className="w-4 h-4 text-amber-400" />
                  <AlertDescription className="text-amber-300 text-sm">
                    <strong>Important:</strong> Email/SMS services are not yet integrated. 
                    Please copy and share this temporary password with the user securely.
                  </AlertDescription>
                </Alert>

                <div className="p-4 rounded-lg bg-slate-800/50 border border-white/10">
                  <Label className="text-slate-400 text-xs mb-2 block">Temporary Password</Label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-[#C5A059] font-mono text-lg bg-slate-900 px-3 py-2 rounded border border-[#C5A059]/30">
                      {resetResult.temporary_password}
                    </code>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={handleCopyPassword}
                      className="border-[#C5A059]/30 hover:bg-[#C5A059]/10"
                      data-testid="copy-temp-password"
                    >
                      {copied ? (
                        <Check className="w-4 h-4 text-emerald-400" />
                      ) : (
                        <Copy className="w-4 h-4 text-[#C5A059]" />
                      )}
                    </Button>
                  </div>
                </div>

                {resetResult.force_change_on_login && (
                  <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                    <p className="text-emerald-400 text-sm flex items-center gap-2">
                      <Check className="w-4 h-4" />
                      User will be required to change password on next login
                    </p>
                  </div>
                )}

                <Button
                  type="button"
                  onClick={handleClose}
                  className="w-full btn-gold"
                >
                  Done
                </Button>
              </>
            ) : (
              // When email is working
              <div className="text-center py-4">
                <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-emerald-400" />
                </div>
                <p className="text-white font-medium mb-2">{resetResult.message}</p>
                <p className="text-slate-400 text-sm">
                  The temporary password has been sent to the user&apos;s email.
                </p>
                <Button
                  type="button"
                  onClick={handleClose}
                  className="mt-4 btn-gold"
                >
                  Done
                </Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AdminResetPasswordModal;
